import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { twinApi, telemetryApi } from '../services/apiClient';
import { useAuth } from './AuthContext';
import { alertBackgroundService } from '../services/alertBackgroundService';

// ── Types ──────────────────────────────────────────────────────
export interface Sensor {
  sensorId: string;
  name: string;
  type: string;
  value: number;
  min: number;
  max: number;
  unit: string;
}

export interface Machine {
  machineId: string;
  name: string;
  type: string;
  status: 'running' | 'idle' | 'fault';
  sensors: Sensor[];
}

export interface Twin {
  id: string;
  name: string;
  company: string;
  factoryType: 'cookie' | 'pharma' | 'fmcg' | 'custom';
  machines: Machine[];
  simulatedMachineData: { [machineId: string]: { [sensorId: string]: number } };
  anomalies: string[];
  healthScore: number;
  lastUpdated: Date;
  createdAt: Date;
  modelUrl?: string;
  ownerEmail?: string;
}

interface TwinContextType {
  isDemo: boolean;
  setDemoMode: (on: boolean) => void;
  activeTwinId: string | null;
  twins: Twin[];
  getActiveTwin: () => Twin | null;
  setActiveTwin: (id: string) => void;
  createTwin: (twin: Twin) => void;
  deleteTwin: (id: string) => void;
  updateTwinData: (id: string, data: Partial<Twin>) => void;
  loading: boolean;
  synced: boolean;
  userEmail: string | null;
}

const TwinContext = createContext<TwinContextType | undefined>(undefined);

const getCacheKey = (email: string | null) => email ? `user_twins_${email}` : 'user_twins';
const getActiveKey = (email: string | null) => email ? `active_twin_id_${email}` : 'active_twin_id';

// ── Demo twin (memory only) ───────────────────────────────────
const DEMO_TWIN: Twin = {
  id: 'demo-cookie-factory',
  name: 'Cookie Factory',
  company: 'CookieFactory Demo',
  factoryType: 'cookie',
  machines: [
    {
      machineId: 'mixer-01', name: 'Mixer', type: 'mixer', status: 'running',
      sensors: [
        { sensorId: 'temp-01', name: 'Temperature', type: 'temperature', value: 78, min: 20, max: 80, unit: '°C' },
        { sensorId: 'rpm-01', name: 'RPM', type: 'speed', value: 4900, min: 0, max: 5000, unit: 'RPM' },
        { sensorId: 'vibration-01', name: 'Vibration', type: 'vibration', value: 9.5, min: 0, max: 10, unit: 'mm/s' },
      ],
    },
    {
      machineId: 'oven-01', name: 'Oven', type: 'oven', status: 'running',
      sensors: [
        { sensorId: 'temp-02', name: 'Temperature', type: 'temperature', value: 245, min: 100, max: 250, unit: '°C' },
        { sensorId: 'humidity-01', name: 'Humidity', type: 'humidity', value: 59, min: 20, max: 60, unit: '%' },
        { sensorId: 'energy-01', name: 'Power', type: 'energy', value: 29, min: 0, max: 30, unit: 'kW' },
      ],
    },
    {
      machineId: 'conveyor-01', name: 'Conveyor', type: 'conveyor', status: 'idle',
      sensors: [
        { sensorId: 'speed-01', name: 'Belt Speed', type: 'speed', value: 0, min: 0, max: 3, unit: 'm/s' },
        { sensorId: 'load-01', name: 'Load', type: 'weight', value: 98, min: 0, max: 100, unit: 'kg' },
      ],
    },
    {
      machineId: 'pump-01', name: 'Pump', type: 'pump', status: 'fault',
      sensors: [
        { sensorId: 'pressure-01', name: 'Pressure', type: 'pressure', value: 95, min: 0, max: 100, unit: 'bar' },
        { sensorId: 'flow-01', name: 'Flow Rate', type: 'flow', value: 85, min: 0, max: 100, unit: 'L/min' },
      ],
    },
    {
      machineId: 'compressor-01', name: 'Compressor', type: 'compressor', status: 'running',
      sensors: [
        { sensorId: 'temp-03', name: 'Temperature', type: 'temperature', value: 75, min: 20, max: 80, unit: '°C' },
        { sensorId: 'pressure-02', name: 'Pressure', type: 'pressure', value: 92, min: 0, max: 100, unit: 'bar' },
      ],
    },
  ],
  simulatedMachineData: {},
  anomalies: ['⚠ Pump pressure exceeding safe limits', '⚠ Conveyor load near maximum capacity'],
  healthScore: 100,
  lastUpdated: new Date(),
  createdAt: new Date(),
};

// ── Helpers ────────────────────────────────────────────────────
const loadCache = (email: string | null): Twin[] => {
  try {
    const key = getCacheKey(email);
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw).map((t: any) => ({
      ...t, lastUpdated: new Date(t.lastUpdated), createdAt: new Date(t.createdAt),
    }));
  } catch { return []; }
};

const saveCache = (twins: Twin[], email: string | null) => {
  const key = getCacheKey(email);
  localStorage.setItem(key, JSON.stringify(twins));
};

const simulateValue = (cur: number, min: number, max: number, baseline: number) =>
  Math.max(min, Math.min(max,
    cur + 0.1 * (baseline - cur) + (Math.random() - 0.5) * 0.5
  ));

const calcHealth = (simData: Record<string, Record<string, number>>, machines: Machine[]) => {
  let total = 0, count = 0;
  const anomalies: string[] = [];
  machines.forEach(m => {
    m.sensors.forEach(s => {
      const v = simData[m.machineId]?.[s.sensorId] ?? s.value;
      const threshold = s.min + (s.max - s.min) * 0.95;
      if (v > threshold || v < s.min) {
        anomalies.push(`⚠ ${m.name} - ${s.name}: ${v.toFixed(1)}${s.unit}`);
      } else {
        total += 100;
      }
      count++;
    });
  });
  return { score: count > 0 ? Math.round(total / count) : 100, anomalies };
};

const initializeAlertsForTwin = (twin: Twin): Twin => {
  const sim: Record<string, Record<string, number>> = {};
  twin.machines.forEach(m => {
    sim[m.machineId] = {};
    m.sensors.forEach(s => {
      const range = s.max - s.min;
      sim[m.machineId][s.sensorId] = s.min + range * (0.80 + Math.random() * 0.15);
    });
  });
  return { ...twin, simulatedMachineData: sim };
};

const apiToTwin = (ws: any, ownerEmail: string): Twin => ({
  id: ws.workspaceId,
  name: ws.name || ws.company || ws.workspaceId,
  company: ws.company || ws.name || '',
  factoryType: (ws.factoryType || 'custom') as any,
  machines: (ws.machines || []).map((m: any, idx: number) => ({
    machineId: m.machineId || m.id || `machine-${idx}`,
    name: m.name || `Machine ${idx + 1}`,
    type: m.type || m.name || 'Other',
    status: 'idle' as const,
    sensors: (m.sensors || []).map((s: any, sidx: number) => {
      if (typeof s === 'string') {
        return {
          sensorId: `${m.machineId || m.id || idx}-s${sidx}`,
          name: s, type: s.toLowerCase(),
          value: 50, min: 0, max: 100, unit: s.includes('Temp') ? '°C' : s.includes('Speed') ? 'RPM' : '%',
        };
      }
      return {
        sensorId: s.sensorId || `s-${idx}-${sidx}`,
        name: s.name || s.type || 'Sensor',
        type: s.type || 'temperature',
        value: s.value ?? 50, min: s.min ?? 0, max: s.max ?? 100,
        unit: s.unit || '',
      };
    }),
  })),
  simulatedMachineData: {},
  anomalies: [],
  healthScore: ws.healthScore ?? 100,
  lastUpdated: new Date(ws.updatedAt || ws.createdAt || Date.now()),
  createdAt: new Date(ws.createdAt || Date.now()),
  ownerEmail,
});

// ── Provider ───────────────────────────────────────────────────
export const TwinProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const userEmail = user?.email || null;

  const [isDemo, setIsDemo] = useState(false);
  const [demoTwins, setDemoTwins] = useState<Twin[]>([DEMO_TWIN]);
  const [userTwins, setUserTwins] = useState<Twin[]>([]);
  const [activeTwinId, setActiveTwinIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [synced, setSynced] = useState(false);
  const telemetryTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Reload cache when user changes ─────────────────────────
  useEffect(() => {
    if (!userEmail) {
      setUserTwins([]);
      setActiveTwinIdState(null);
      return;
    }
    const cached = loadCache(userEmail);
    setUserTwins(cached);
    const savedActive = localStorage.getItem(getActiveKey(userEmail));
    setActiveTwinIdState(savedActive || (cached.length > 0 ? cached[0].id : null));
  }, [userEmail]);

  // ── Fetch twins from DynamoDB on mount (non-demo) ───────────
  useEffect(() => {
    if (isDemo || !userEmail) return;
    setLoading(true);
    twinApi.listWorkspaces()
      .then((remote: any) => {
        const list = Array.isArray(remote) ? remote : [];
        if (list.length === 0) { setLoading(false); setSynced(true); return; }
        const remoteTwins = list.map((ws: any) => apiToTwin(ws, userEmail)).map(initializeAlertsForTwin);
        setUserTwins(prev => {
          const localMap = new Map(prev.map(t => [t.id, t]));
          const merged = remoteTwins.map(rt => {
            const local = localMap.get(rt.id);
            return local ? { ...rt, modelUrl: local.modelUrl || rt.modelUrl } : rt;
          });
          const remoteIds = new Set(remoteTwins.map(t => t.id));
          const localOnly = prev.filter(t => !remoteIds.has(t.id));
          const final = [...merged, ...localOnly];
          saveCache(final, userEmail);
          return final;
        });
        setSynced(true);
        setLoading(false);
      })
      .catch(() => { setLoading(false); setSynced(true); });
  }, [isDemo, userEmail]);

  // ── Demo mode toggle ────────────────────────────────────────
  const setDemoMode = useCallback((on: boolean) => {
    setIsDemo(on);
    if (on) {
      setActiveTwinIdState('demo-cookie-factory');
    } else {
      const cached = loadCache(userEmail);
      setActiveTwinIdState(cached.length > 0 ? cached[0].id : null);
    }
  }, [userEmail]);

  const twins = isDemo ? demoTwins : userTwins;

  // Cache user twins locally
  useEffect(() => {
    if (!isDemo && userTwins.length > 0) saveCache(userTwins, userEmail);
  }, [userTwins, isDemo, userEmail]);

  // Persist active twin id
  useEffect(() => {
    const key = getActiveKey(userEmail);
    if (activeTwinId) localStorage.setItem(key, activeTwinId);
    else localStorage.removeItem(key);
  }, [activeTwinId, userEmail]);

  // ── Sensor simulation ───────────────────────────────────────
  useEffect(() => {
    const iv = setInterval(() => {
      const setter = isDemo ? setDemoTwins : setUserTwins;
      setter(prev => prev.map(twin => {
        let sim = twin.simulatedMachineData;
        if (!sim || Object.keys(sim).length === 0) {
          sim = {};
          twin.machines.forEach(m => {
            sim[m.machineId] = {};
            m.sensors.forEach(s => { sim[m.machineId][s.sensorId] = s.value; });
          });
        }
        const updated: Record<string, Record<string, number>> = {};
        Object.keys(sim).forEach(mid => { updated[mid] = { ...sim[mid] }; });
        twin.machines.forEach(m => {
          if (!updated[m.machineId]) updated[m.machineId] = {};
          m.sensors.forEach(s => {
            updated[m.machineId][s.sensorId] = simulateValue(
              updated[m.machineId][s.sensorId] ?? s.value, s.min, s.max, s.value
            );
          });
        });
        const { score, anomalies } = calcHealth(updated, twin.machines);
        const updatedMachines = twin.machines.map(m => {
          const hasCrit = m.sensors.some(s => {
            const v = updated[m.machineId]?.[s.sensorId] ?? s.value;
            return v > s.min + (s.max - s.min) * 0.95 || v < s.min;
          });
          const status: 'running' | 'idle' | 'fault' = hasCrit ? 'fault' : 'running';
          return m.status !== status ? { ...m, status } : m;
        });
        return { ...twin, machines: updatedMachines, simulatedMachineData: updated, healthScore: score, anomalies, lastUpdated: new Date() };
      }));
    }, 2000);
    return () => clearInterval(iv);
  }, [isDemo]);

  // ── Start background alert service ────────────────────────
  useEffect(() => {
    if (userEmail) {
      alertBackgroundService.setUserEmail(userEmail);
      alertBackgroundService.start();
      console.log('[TwinProvider] Background alert service started');
    } else {
      alertBackgroundService.stop();
      console.log('[TwinProvider] Background alert service stopped');
    }

    return () => {
      // Don't stop on unmount - keep running in background
    };
  }, [userEmail]);

  // ── Update background service with twin data ────────────────
  useEffect(() => {
    if (activeTwinId) {
      const activeTwin = twins.find(t => t.id === activeTwinId);
      if (activeTwin) {
        alertBackgroundService.setTwinData(activeTwin);
      }
    }
  }, [activeTwinId, twins]);

  // ── Push telemetry to DynamoDB every 15s (non-demo only) ────
  useEffect(() => {
    if (telemetryTimer.current) clearInterval(telemetryTimer.current);
    if (isDemo || !userEmail) return;

    telemetryTimer.current = setInterval(() => {
      const active = userTwins.find(t => t.id === activeTwinId) || userTwins[0];
      if (!active || active.id === 'demo-cookie-factory') return;

      active.machines.forEach(m => {
        const sensorData = m.sensors.map(s => ({
          sensorId: s.sensorId,
          type: s.type,
          value: +(active.simulatedMachineData?.[m.machineId]?.[s.sensorId] ?? s.value).toFixed(2),
          unit: s.unit,
        }));
        if (sensorData.length > 0) {
          telemetryApi.ingest(active.id, m.machineId, sensorData).catch(() => {});
        }
      });

      twinApi.updateWorkspace(active.id, { healthScore: active.healthScore }).catch(() => {});
    }, 15000);

    return () => { if (telemetryTimer.current) clearInterval(telemetryTimer.current); };
  }, [isDemo, activeTwinId, userTwins, userEmail]);

  // ── CRUD ────────────────────────────────────────────────────
  const getActiveTwin = useCallback((): Twin | null => {
    if (!activeTwinId) return twins.length > 0 ? twins[0] : null;
    return twins.find(t => t.id === activeTwinId) || (twins.length > 0 ? twins[0] : null);
  }, [twins, activeTwinId]);

  const setActiveTwin = useCallback((id: string) => {
    if (twins.some(t => t.id === id)) setActiveTwinIdState(id);
  }, [twins]);

  const createTwin = useCallback((twin: Twin) => {
    const twinWithOwner: Twin = { ...twin, ownerEmail: userEmail || undefined };
    
    // Save to backend if not demo
    if (!isDemo && userEmail) {
      twinApi.create({
        company: twin.company,
        factoryType: twin.factoryType,
        machines: twin.machines,
        sensors: twin.machines.flatMap(m => m.sensors.map(s => s.name)),
      }).catch(err => console.error('[TwinContext] Failed to save twin to backend:', err));
    }
    
    setUserTwins(prev => {
      const next = [...prev, twinWithOwner];
      saveCache(next, userEmail);
      return next;
    });
    setActiveTwinIdState(twin.id);
  }, [userEmail, isDemo]);

  const deleteTwin = useCallback((id: string) => {
    if (isDemo) return;
    twinApi.deleteWorkspace(id).catch(() => {});
    setUserTwins(prev => {
      const next = prev.filter(t => t.id !== id);
      saveCache(next, userEmail);
      return next;
    });
    if (activeTwinId === id) setActiveTwinIdState(null);
  }, [activeTwinId, isDemo, userEmail]);

  const updateTwinData = useCallback((id: string, data: Partial<Twin>) => {
    const setter = isDemo ? setDemoTwins : setUserTwins;
    setter(prev => prev.map(t => t.id === id ? { ...t, ...data, lastUpdated: new Date() } : t));
  }, [isDemo]);

  return (
    <TwinContext.Provider value={{
      isDemo, setDemoMode,
      activeTwinId, twins,
      getActiveTwin, setActiveTwin,
      createTwin, deleteTwin, updateTwinData,
      loading, synced,
      userEmail,
    }}>
      {children}
    </TwinContext.Provider>
  );
};

export const useTwin = (): TwinContextType => {
  const ctx = useContext(TwinContext);
  if (!ctx) throw new Error('useTwin must be used within TwinProvider');
  return ctx;
};
